import type { HandwritingIndex } from "./handwriting-lookup.js";
import { readShapeLine } from "./handwriting-line.js";

/**
 * Reading the published handwriting index.
 *
 * The file is what `src/assets/handwriting-data.ts` wrote, one line per
 * character, and this turns it into the array `lookUpHandwriting` scans. The
 * browser fetches it the first time a reader opens the pad and holds it for as
 * long as the page is open.
 *
 * **Parsed once into plain objects, and the shape of them is the budget.**
 * 9,276 characters come to 143,425 runs, and a run held as an object of three
 * small integers is the difference between an index a phone can hold and one it
 * cannot. Anything richer per run (a class, a Float64Array per stroke, the
 * decoded letters kept alongside) multiplies by 143,425.
 *
 * **The file opens with comment lines and they are skipped here.** The index is
 * derived from Arphic's stroke data rather than copied from it, so the Arphic
 * Public License asks for a notice of the change, and a notice travelling with
 * the file is the one that reaches whoever has the file. See
 * `src/assets/handwriting-notice.ts`.
 *
 * A line the reader's copy has lost is dropped and the rest is kept. See
 * `readShapeLine`.
 *
 * **Which characters the file covers is answered without describing any of
 * them.** A handwriting card is introduced only for a character the site can
 * mark, and that question is asked of every character of every word a reader
 * holds at the start of a session. Describing nine thousand characters'
 * strokes to answer it would be a poor trade, so {@link handwritingCharacters}
 * reads the first character of each line and stops there. See
 * `src/user/handwriting-strokes.ts`.
 */

/** What opens a line that says something about the file rather than a character. */
const commentMark = "#";

/** Whether a line of the file describes a character at all. */
function describesACharacter(line: string): boolean {
  return line !== "" && !line.startsWith(commentMark);
}

/** Turns the published file into the index a lookup scans. */
export function readHandwritingIndex(text: string): HandwritingIndex {
  const entries = [];

  for (const line of text.split("\n")) {
    if (!describesACharacter(line)) {
      continue;
    }

    const read = readShapeLine(line);

    if (read !== undefined) {
      entries.push(read);
    }
  }

  return entries;
}

/**
 * Which characters the file covers, without describing any of their strokes.
 *
 * By code point rather than by UTF-16 unit, for the reason `readShapeLine`
 * gives: several of these are outside the basic multilingual plane and half a
 * surrogate pair is not a character anybody wrote.
 *
 * A line too short to carry a stroke is left out, which is what keeps a
 * truncated download from answering for a character it has no description of.
 *
 * @param text - The published file.
 * @returns Every character it describes.
 */
export function handwritingCharacters(text: string): ReadonlySet<string> {
  const characters = new Set<string>();

  for (const line of text.split("\n")) {
    const code = describesACharacter(line) ? line.codePointAt(0) : undefined;
    const character = code === undefined ? "" : String.fromCodePoint(code);

    if (character !== "" && line.length > character.length) {
      characters.add(character);
    }
  }

  return characters;
}
